import { useEffect, useState } from 'react';
import { CheckCircle2, X, PartyPopper } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { JourneyPhase } from '@/hooks/useJourneyProgress';

interface PhaseCompletionBannerProps {
  phases: JourneyPhase[];
}

const STORAGE_PREFIX = 'meq_staging_phase_celebrated_';

/**
 * Shows a celebration banner when a phase transitions to complete.
 * Uses localStorage to track which phases have been celebrated to avoid repeats.
 */
export const PhaseCompletionBanner = ({ phases }: PhaseCompletionBannerProps) => {
  const [celebratedPhase, setCelebratedPhase] = useState<JourneyPhase | null>(null);

  useEffect(() => {
    // Find a phase that is newly complete (not yet celebrated)
    for (const phase of phases) {
      if (phase.isComplete) {
        const key = `${STORAGE_PREFIX}${phase.id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, 'true');
          setCelebratedPhase(phase);
          // Auto-dismiss after 8 seconds
          const timer = setTimeout(() => setCelebratedPhase(null), 8000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [phases]);

  if (!celebratedPhase) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 mb-4',
        'animate-in slide-in-from-top-2 fade-in duration-500',
      )}
    >
      <div className="shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
        <PartyPopper className="w-4 h-4 text-green-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-green-700 dark:text-green-400">
          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
          {celebratedPhase.name} complete!
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {celebratedPhase.nextStepPrompt}
        </p>
      </div>
      <button
        onClick={() => setCelebratedPhase(null)}
        className="shrink-0 p-1 rounded-md hover:bg-muted transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </button>
    </div>
  );
};
